import { TextTooltip } from '@components'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import {
  dashboardQueryKey,
  dashboardQueryOptions,
  fetchBalanceForecast,
  IAccountBalance
} from '@query'
import useAccountBalance from '@root/query/hooks/useAccountBalance'
import { formatCurrency, formatDate } from '@utils'
import { useQuery } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaQuestionCircle } from 'react-icons/fa'
import styled from 'styled-components'
import { AskLuca } from '../shared/AskLuca'
import { CardSurface } from '../shared/Card'
import { ScrubPoint } from './BalanceSparkline'
import { CashAccountMenu } from './CashAccountMenu'
import { CashAccounts, COLLAPSED_ACCOUNT_COUNT } from './CashAccounts'
import { CashEmptyState } from './CashEmptyState'
import { CashHeroChart } from './CashHeroChart'
import { WHEN_NARROW, WHEN_STACKED } from '../../layout'

// Bank accounts are keyed by the integration row, not `balance.account_id`.
// Several banks can share one bookkeeping account; using that id made the
// selector stick on the first match. The forecast endpoint still filters on
// `balance.account_id`.
const accountKey = (account: IAccountBalance) =>
  `${account.resource.type}:${account.resource.id}:${account.identifier}`

export const CashCard: React.FC = () => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const canViewAccounts = usePermission(PermissionSet.ACCOUNTS_VIEW)
  const { data: accounts, isLoading: isAccountsLoading } = useAccountBalance()
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [scrubbed, setScrubbed] = useState<ScrubPoint | null>(null)

  const allAccounts = useMemo(() => accounts ?? [], [accounts])
  const hasAccounts = allAccounts.length > 0
  const selected = allAccounts.find(account => accountKey(account) === selectedKey) ?? null

  const forecastQueryKey = dashboardQueryKey(
    slug,
    'balanceForecast',
    selected?.balance.account_id ?? null
  )
  const { data: forecast, isLoading: isForecastLoading } = useQuery({
    queryKey: forecastQueryKey,
    queryFn: () => fetchBalanceForecast({ slug, accountId: selected?.balance.account_id ?? null }),
    ...{
      enabled: Boolean(slug) && canViewAccounts && hasAccounts,
      ...dashboardQueryOptions(slug),
      staleTime: 1000 * 60 * 15
    }
  })

  if (!isAccountsLoading && accounts?.length === 0) {
    return <CashEmptyState />
  }

  const shownAccounts = selected ? [selected] : allAccounts
  const visibleAccounts =
    selected || expanded ? shownAccounts : shownAccounts.slice(0, COLLAPSED_ACCOUNT_COUNT)
  const hiddenAccounts = selected ? [] : shownAccounts.slice(COLLAPSED_ACCOUNT_COUNT)

  const total = shownAccounts.reduce((sum, account) => sum + account.balance.amount, 0)
  const days = forecast?.days ?? []
  const showChart = days.length > 1
  const showChartSkeleton = isForecastLoading && !showChart
  const todayDay = days.find(day => day.current) ?? days[0]
  const lastDay = days[days.length - 1]
  const firstDay = days[0]

  const changeRatio =
    firstDay && todayDay && firstDay.balance !== 0
      ? (todayDay.balance - firstDay.balance) / Math.abs(firstDay.balance)
      : null

  const title = scrubbed
    ? scrubbed.isForecast
      ? t('dashboard.cash.titleForecast')
      : t('dashboard.cash.titleActual')
    : selected
    ? t('dashboard.cash.titleSingle')
    : t('dashboard.cash.title')

  const displayTotal = scrubbed ? scrubbed.day.balance : total

  const subline = scrubbed
    ? t(scrubbed.isForecast ? 'dashboard.cash.scrubForecast' : 'dashboard.cash.scrubActual', {
        date: formatDate(scrubbed.day.date)
      })
    : lastDay
    ? t('dashboard.cash.forecastOn', {
        date: formatDate(lastDay.date),
        amount: formatCurrency(lastDay.balance)
      })
    : ''

  return (
    <HeroSurface>
      <TopRow>
        <HeroLabel>{title}</HeroLabel>
        <TextTooltip tooltip={t('dashboard.cash.tooltip')} placement="bottom">
          <HeroHelpIcon />
        </TextTooltip>
        <Spacer />
        <CashAccountMenu
          accounts={allAccounts}
          selected={selected}
          accountKey={accountKey}
          onSelect={setSelectedKey}
        />
        <AskLuca
          onNavy
          placement="bottom-end"
          prompts={t('dashboard.cash.ask.prompts', { returnObjects: true }) as string[]}
        />
      </TopRow>

      <HeroTotal>{formatCurrency(displayTotal)}</HeroTotal>

      <DeltaRow>
        {changeRatio !== null && (
          <DeltaPill $positive={changeRatio >= 0}>
            {changeRatio >= 0 ? '+' : ''}
            {(changeRatio * 100).toLocaleString('fi-FI', { maximumFractionDigits: 1 })} %
          </DeltaPill>
        )}
        <HeroSubline>{subline}</HeroSubline>
      </DeltaRow>

      {(showChartSkeleton || showChart) && (
        <CashHeroChart
          days={days}
          isSkeleton={showChartSkeleton}
          updatedOn={shownAccounts[0]?.balance?.date ?? todayDay?.date}
          onScrub={setScrubbed}
        />
      )}

      <CashAccounts
        isLoading={isAccountsLoading}
        visibleAccounts={visibleAccounts}
        hiddenAccounts={hiddenAccounts}
        expanded={expanded}
        selected={Boolean(selected)}
        onToggle={() => setExpanded(current => !current)}
        accountKey={accountKey}
      />
    </HeroSurface>
  )
}

const HeroSurface = styled(CardSurface)`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  height: 100%;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;
  background: ${({ theme }) => theme.colors.menuBg};
  color: ${({ theme }) => theme.colors.neutralWhite};

  ${WHEN_NARROW} {
    padding: ${({ theme }) => theme.spacing.md}rem;
  }

  ${WHEN_STACKED} {
    height: auto;
  }
`

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const HeroLabel = styled.span`
  flex: 0 0 auto;
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.dashboard.onNavyLabel};
  white-space: nowrap;
`

const HeroHelpIcon = styled(FaQuestionCircle)`
  color: ${({ theme }) => theme.dashboard.onNavyLabel};
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  cursor: help;
`

const Spacer = styled.div.attrs({ ['aria-hidden']: true })`
  flex: 1;
`

const HeroTotal = styled.span`
  min-height: 2.25rem;
  font-size: 2.125rem;

  ${WHEN_NARROW} {
    font-size: 1.9375rem;
  }

  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
`

const DeltaRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const DeltaPill = styled.span<{ $positive: boolean }>`
  padding: 0.125rem ${({ theme }) => theme.spacing.sm}rem;
  border-radius: 999px;
  background: ${({ theme, $positive }) =>
    $positive ? theme.dashboard.positive : theme.dashboard.critical};
  color: ${({ theme }) => theme.colors.neutralWhite};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 700;
  white-space: nowrap;
`

const HeroSubline = styled.span`
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.dashboard.onNavyMuted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
